import { useState } from 'react';
import type { LoanEvent } from '@/types';
import { EVENT_TYPE_LABELS } from '@/types';
import { softDeleteEvent } from '@/lib/db';
import { summarizeEvent, eventDateLabel } from '../../events/eventDisplay';
import { formatDateTime } from '@/lib/format';

interface EventDetailSheetProps {
  event: LoanEvent;
  onClose: () => void;
  onEdit: () => void;
  onDeleted: () => void;
}

export function EventDetailSheet({ event, onClose, onEdit, onDeleted }: EventDetailSheetProps) {
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const isSuperseded = !!event.supersededAt;

  async function handleDelete() {
    setIsDeleting(true);
    try {
      await softDeleteEvent(event.type, event.id);
      onDeleted();
      onClose();
    } finally {
      setIsDeleting(false);
    }
  }

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-handle" />
        <div className="sheet-header">
          <h2 style={{ margin: 0 }}>{EVENT_TYPE_LABELS[event.type]}</h2>
          <button className="close-btn" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="stack">
          <div className="card">
            <p style={{ margin: 0, color: 'var(--color-text)' }}>{summarizeEvent(event)}</p>
            <p className="field-hint" style={{ marginTop: 8 }}>
              {eventDateLabel(event)}
            </p>
            {event.note && <p className="field-hint">Note: {event.note}</p>}
            <p className="field-hint">Entered {formatDateTime(event.createdAt)}</p>
            {isSuperseded && (
              <p className="field-error">
                This event was superseded on {formatDateTime(event.supersededAt)}
                {event.supersededReason ? ` (${event.supersededReason})` : ''}. It is kept for audit history and no
                longer affects calculations.
              </p>
            )}
          </div>

          {!isSuperseded && !confirmingDelete && (
            <div className="row">
              <button className="btn" onClick={onEdit}>
                Edit
              </button>
              <button className="btn btn-danger" onClick={() => setConfirmingDelete(true)}>
                Delete
              </button>
            </div>
          )}

          {confirmingDelete && (
            <div className="card" style={{ borderColor: 'var(--color-danger)' }}>
              <p style={{ color: 'var(--color-text)' }}>
                This will remove the event from your active loan history. The record itself is kept for audit
                purposes, but it will no longer affect any calculation. Continue?
              </p>
              <div className="row">
                <button className="btn" onClick={() => setConfirmingDelete(false)} disabled={isDeleting}>
                  Cancel
                </button>
                <button className="btn btn-danger" onClick={handleDelete} disabled={isDeleting}>
                  {isDeleting ? 'Deleting…' : 'Confirm Delete'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
